import { useGSAP } from '@gsap/react';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { useRef, useState } from 'react';

import { CHAPTER_KEYS, type ChapterKey, isChapterKey } from '@/core/chapters';
import { useContent } from '@/content/ContentContext';

import styles from './JourneyRail.module.css';

/**
 * Le repère de voyage, comme un instrument de bord : les sept chapitres, celui
 * où l'on se trouve (et le nom de son trou noir), et la distance parcourue.
 * Chaque repère est un lien : on peut sauter d'un monde à l'autre.
 */
export function JourneyRail() {
  const { journey } = useContent();
  const railRef = useRef<HTMLElement>(null);
  const [active, setActive] = useState<ChapterKey>('origin');

  useGSAP(
    () => {
      const rail = railRef.current;
      if (!rail) return;
      const chapters = Array.from(document.querySelectorAll<HTMLElement>('[data-cosmos-chapter]'));

      // Le chapitre en cours : le dernier dont le haut a franchi le milieu de l'écran.
      // Lu à chaque défilement, il reste juste même après un saut par lien.
      const locate = (): void => {
        const center = window.innerHeight / 2;
        let current: ChapterKey = 'origin';
        for (const chapter of chapters) {
          const key = chapter.dataset.cosmosChapter;
          if (chapter.getBoundingClientRect().top <= center && isChapterKey(key)) current = key;
        }
        setActive(current);
      };
      const trigger = ScrollTrigger.create({
        start: 0,
        end: 'max',
        onUpdate: (self) => {
          rail.style.setProperty('--journey', self.progress.toFixed(4));
          locate();
        },
        onRefresh: locate,
      });
      return () => {
        trigger.kill();
      };
    },
    { scope: railRef },
  );

  return (
    <nav ref={railRef} className={styles.rail} aria-label={journey.railLabel}>
      <span className={styles.track} aria-hidden="true" />
      <ol className={styles.list}>
        {CHAPTER_KEYS.map((key) => {
          const chapter = journey.chapters[key];
          return (
            <li key={key}>
              <a
                className={styles.link}
                href={chapter.href}
                aria-current={active === key ? 'step' : undefined}
              >
                <span className={styles.label}>
                  {chapter.title}
                  <span className={styles.star} aria-hidden="true">
                    {journey.starLabel} {chapter.star}
                  </span>
                </span>
                <span className={styles.index} aria-hidden="true">
                  {chapter.index}
                </span>
              </a>
            </li>
          );
        })}
      </ol>
    </nav>
  );
}
